import { networkApi } from '../infrastructure/api/NetworkApiClient';
import type {
  AddressHit,
  ConnectionProfile,
  ConnectionProfileEstimate,
  ConnectionRequest,
  GreenfieldConnection,
  LoadCaseMode,
  NetzanschlussRemedyResult,
  RequestedAsset,
} from '../types/netzanschluss';
import type { PlanningMeasure } from '../types/scenario';

/**
 * API layer of the Netzanschlussprüfung module. Zone-scoped calls go through
 * networkApi.zone* (automatic /zones/{id} prefix, auth + tenant headers); the
 * portal inbox endpoints are global (unassigned requests have no zone yet).
 * Errors propagate as ApiError so the UI can toast them.
 */
/** loadCaseMode omitted = the deployment default from the settings. */
/** measures = what-if grid measures the check is evaluated WITH (verdict conditional on them). */
export type CheckBody =
  | { hausanschlussId: string; assets: RequestedAsset[]; persist: boolean; loadCaseMode?: LoadCaseMode; measures?: PlanningMeasure[] }
  | { newConnection: GreenfieldConnection; assets: RequestedAsset[]; persist: boolean; loadCaseMode?: LoadCaseMode; measures?: PlanningMeasure[] };

export const netzanschlussService = {
  runCheck(body: CheckBody): Promise<ConnectionRequest> {
    return networkApi.zonePost<ConnectionRequest>('/netzanschluss/check', body);
  },

  /** "Abhilfe finden": check (not persisted), candidate measures against the failing criteria, each re-checked. */
  findRemedies(body: CheckBody): Promise<NetzanschlussRemedyResult> {
    return networkApi.zonePost<NetzanschlussRemedyResult>('/netzanschluss/remedies', { ...body, persist: false });
  },

  listRequests(): Promise<ConnectionRequest[]> {
    return networkApi.zoneGet<ConnectionRequest[]>('/netzanschluss/requests');
  },

  listUnassignedRequests(): Promise<ConnectionRequest[]> {
    return networkApi.httpGet<ConnectionRequest[]>('/netzanschluss/requests/unassigned');
  },

  /** hausanschlussId empty for greenfield requests (zone-only assignment). */
  assignRequest(id: string, zoneId: string, hausanschlussId?: string): Promise<ConnectionRequest> {
    return networkApi.httpPost<ConnectionRequest>(`/netzanschluss/requests/${id}/assign`, {
      zoneId,
      hausanschlussId: hausanschlussId ?? '',
    });
  },

  recheckRequest(id: string): Promise<ConnectionRequest> {
    return networkApi.zonePost<ConnectionRequest>(`/netzanschluss/requests/${id}/recheck`);
  },

  decideRequest(id: string, decision: 'APPROVED' | 'REJECTED'): Promise<void> {
    return networkApi.zonePost<void>(`/netzanschluss/requests/${id}/decide`, { decision });
  },

  /** Load assumptions of a new building from use + size (pure computation, zone-independent). */
  estimateProfile(profile: ConnectionProfile): Promise<ConnectionProfileEstimate> {
    return networkApi.httpPost<ConnectionProfileEstimate>('/netzanschluss/profile-estimate', profile);
  },

  /** Address → candidate points for the Neubau pin (zone addresses first, then the geocoder). */
  searchAddress(query: string): Promise<AddressHit[]> {
    return networkApi.zoneGet<AddressHit[]>(`/netzanschluss/address-search?q=${encodeURIComponent(query)}`);
  },
};
